"use client";

import { ArrowRight, Cpu, Settings, Wrench, MoveDiagonal } from "lucide-react";
import { homeTheme } from "@/lib/theme/home-theme";
import { useLanguage } from "@/lib/i18n/context";
import { motion } from "framer-motion";

const serviceIcons = [MoveDiagonal, Cpu, Wrench, Settings];

// Single brand accent — professional, consistent across all cards
const cardAccent = {
  bar: "bg-orange-500 dark:bg-orange-500",
  icon: "bg-orange-100 dark:bg-orange-950/40 text-orange-600 dark:text-orange-400",
  link: "text-orange-600 dark:text-orange-400",
};

export function ServicesSection() {
  const { t } = useLanguage();

  return (
    <section id="uslugi" className={homeTheme.servicesSection}>
      {/* Decorative ambient orbs */}
      <div
        aria-hidden="true"
        className="absolute top-0 left-1/4 -translate-x-1/2 h-96 w-96 rounded-full opacity-25 dark:opacity-10 blur-[120px] pointer-events-none bg-orange-300 dark:bg-orange-600"
      />
      <div
        aria-hidden="true"
        className="absolute -bottom-32 -right-32 h-96 w-96 rounded-full opacity-15 dark:opacity-10 blur-[120px] pointer-events-none bg-orange-400 dark:bg-orange-500"
      />

      <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
        {/* Section header */}
        <div className="mx-auto max-w-2xl text-center mb-16 md:mb-24">
          <motion.h2
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-base font-semibold leading-7 text-orange-600 dark:text-orange-400 uppercase tracking-widest"
          >
            {t.home.offerTag}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="mt-2 text-4xl font-extrabold tracking-tight text-zinc-900 dark:text-zinc-100 sm:text-5xl text-balance"
          >
            {t.home.offerTitle}
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="mt-6 text-lg leading-8 text-zinc-600 dark:text-zinc-400"
          >
            {t.home.offerDesc}
          </motion.p>
        </div>

        {/* Service cards grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {t.uslugi.list.map((feature, idx) => {
            const Icon = serviceIcons[idx % serviceIcons.length];

            return (
              <motion.div
                key={feature.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className={homeTheme.serviceCard}
              >
                <div className="relative z-10 flex flex-col h-full">
                  <div className="flex items-center justify-between mb-6">
                    <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${cardAccent.icon} group-hover:scale-110 transition-transform`}>
                      <Icon className="h-5 w-5" aria-hidden="true" />
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 dark:text-zinc-500">
                      {feature.tag}
                    </span>
                  </div>

                  <div className="flex-auto">
                    <h3 className="text-lg font-bold leading-6 text-zinc-900 dark:text-zinc-100 mb-2">
                      {feature.name}
                    </h3>
                    <p className="text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
                      {t.home.services[idx].desc}
                    </p>
                  </div>

                  <div className="mt-6 pt-4 border-t border-stone-200/60 dark:border-zinc-700/50">
                    <a
                      href="/uslugi"
                      aria-label={`${t.home.learnMore}: ${feature.name}`}
                      className={`text-xs font-bold leading-6 ${cardAccent.link} flex items-center gap-1 group-hover:gap-2 transition-all cursor-pointer`}
                    >
                      {t.home.learnMore} <ArrowRight className="h-3 w-3" aria-hidden="true" />
                    </a>
                  </div>
                </div>
              </motion.div>
            );
          })}


        </div>
      </div>
    </section>
  );
}

